import { Avatar } from 'antd';

import styles from './user-avatar.module.scss';

export type UserAvatarSize = 'sm' | 'md' | 'lg';

const SIZE_PX: Record<UserAvatarSize, number> = { sm: 18, md: 24, lg: 36 };

export type UserAvatarProps = {
  name: string;
  src?: string;
  /** Preset ('sm'=18, 'md'=24, 'lg'=36) or an explicit pixel diameter. Default 'md'. */
  size?: UserAvatarSize | number;
  /** When set, renders the avatar alongside a text label. */
  label?: string;
  /** Secondary line (role); only shown at the 'lg' layout. */
  subtitle?: string;
  className?: string;
};

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
}

function toPx(size: UserAvatarSize | number): number {
  return typeof size === 'number' ? size : SIZE_PX[size];
}

export function UserAvatar({
  name,
  src,
  size = 'md',
  label,
  subtitle,
  className,
}: UserAvatarProps) {
  const px = toPx(size);
  const avatar = (
    <Avatar
      src={src}
      alt={name}
      size={px}
      className={[styles.avatar, label ? undefined : className].filter(Boolean).join(' ')}
    >
      {initials(name)}
    </Avatar>
  );

  if (!label) return avatar;

  const large = px >= SIZE_PX.lg;
  return (
    <span
      className={[styles.withLabel, large ? styles.lg : styles.sm, className]
        .filter(Boolean)
        .join(' ')}
    >
      {avatar}
      <span className={styles.text}>
        <span className={styles.name}>{label}</span>
        {large && subtitle ? <span className={styles.subtitle}>{subtitle}</span> : null}
      </span>
    </span>
  );
}
